import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, RefreshCw } from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import { useToast, ToastContainer } from '../common/Toast';
import api from '../../services/api';

const STATUS_STAGE_MAP = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  EVALUATION: 'Evaluated',
  AWARDED: 'Awarded',
  CANCELLED: 'Contract',
};

const stageOrder = ['Draft', 'Generated', 'Review', 'Approved', 'Published'];

const normalizeStatus = (status) => String(status || '').toUpperCase();

const deriveStage = (rfp) => {
  const lifecycle = rfp?.metadata?.lifecycleStage;
  if (lifecycle) {
    return lifecycle;
  }
  return STATUS_STAGE_MAP[normalizeStatus(rfp?.status)] || 'Draft';
};

const deriveApprovalStatus = (rfp, stage) => {
  const explicit = rfp?.metadata?.approvalStatus;
  if (explicit) {
    return explicit;
  }

  if (stage === 'Approved') return 'Approved';
  if (stage === 'Review' || stage === 'Generated') return 'Pending';
  return 'Not Submitted';
};

const getSubmitter = (rfp) => {
  if (rfp?.createdBy?.name) return rfp.createdBy.name;
  if (rfp?.createdBy?.email) return rfp.createdBy.email;
  if (typeof rfp?.createdBy === 'string') return rfp.createdBy.slice(-8);
  return '-';
};

const extractRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const nextStageOnApprove = (stage) => {
  const idx = stageOrder.indexOf(stage);
  if (idx === -1 || idx === stageOrder.length - 1) {
    return stage;
  }
  return stageOrder[idx + 1];
};

const statusPill = (status) => {
  const map = {
    Pending: { bg: '#fef3c7', color: '#92400e' },
    Approved: { bg: '#dcfce7', color: '#166534' },
    Rejected: { bg: '#fee2e2', color: '#991b1b' },
    'Not Submitted': { bg: '#f3f4f6', color: '#4b5563' },
  };
  const cfg = map[status] || map['Not Submitted'];

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '999px',
        fontSize: '12px',
        fontWeight: 600,
        background: cfg.bg,
        color: cfg.color,
      }}
    >
      {status}
    </span>
  );
};

const RfpApprovalQueue = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadQueue = async () => {
    setLoading(true);
    try {
      const response = await api.get('/rfp/approvals');
      const rows = extractRows(response);
      setQueue(rows);
    } catch (err) {
      try {
        const fallback = await api.get('/sourcing/rfps');
        const allRfps = extractRows(fallback);
        const derivedRows = allRfps
          .map((rfp) => {
            const stage = deriveStage(rfp);
            return {
              _id: rfp._id,
              rfpNumber: rfp.rfpNumber,
              submittedBy: getSubmitter(rfp),
              stage,
              approvalStatus: deriveApprovalStatus(rfp, stage),
              comments: rfp?.metadata?.approvalComments || '-',
            };
          })
          .filter((row) => row.approvalStatus !== 'Not Submitted');
        setQueue(derivedRows);
      } catch (fallbackErr) {
        showError(
          fallbackErr?.response?.data?.message ||
            fallbackErr.message ||
            'Failed to load approval queue',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQueue();
    // Load once on mount. Avoid reactive dependency loops from unstable toast refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPending = (row) => row.approvalStatus === 'Pending';

  const updateRow = (rfpId, updater) => {
    setQueue((prev) =>
      prev.map((row) => (row._id === rfpId ? updater(row) : row)),
    );
  };

  const handleApprove = useCallback(
    async (row) => {
      try {
        await api.post(`/rfp/${row._id}/approve`, {
          decision: 'approve',
        });
        success('RFP approved successfully.');
        loadQueue();
      } catch (err) {
        const nextStage = nextStageOnApprove(row.stage);
        updateRow(row._id, (current) => ({
          ...current,
          stage: nextStage,
          approvalStatus: 'Approved',
          comments:
            current.comments === '-' ? 'Approved locally' : current.comments,
        }));
        showError(
          err?.response?.data?.message ||
            'Approval API is not available yet. Applied local stage transition.',
        );
      }
    },
    [loadQueue, showError, success],
  );

  const handleReject = useCallback(
    async (row) => {
      try {
        await api.post(`/rfp/${row._id}/approve`, {
          decision: 'reject',
          comments: 'Rejected and sent back to builder',
        });
        success('RFP rejected. Sent back to builder.');
        loadQueue();
      } catch (err) {
        updateRow(row._id, (current) => ({
          ...current,
          stage: 'Draft',
          approvalStatus: 'Rejected',
          comments: 'Rejected and sent back to builder',
        }));
        showError(
          err?.response?.data?.message ||
            'Approval API is not available yet. Applied local rejection transition to builder.',
        );
      }
    },
    [loadQueue, showError, success],
  );

  const columns = useMemo(
    () => [
      {
        key: 'rfpNumber',
        label: 'RFP ID',
        render: (_, row) => <strong>{row.rfpNumber || '-'}</strong>,
      },
      {
        key: 'submittedBy',
        label: 'Submitted By',
      },
      {
        key: 'stage',
        label: 'Stage',
      },
      {
        key: 'approvalStatus',
        label: 'Status',
        render: (_, row) => statusPill(row.approvalStatus),
      },
      {
        key: 'comments',
        label: 'Comments',
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (_, row) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-success"
              disabled={!isPending(row)}
              onClick={(e) => {
                e.stopPropagation();
                handleApprove(row);
              }}
            >
              <CheckCircle2 size={14} className="me-1" />
              Approve
            </button>
            <button
              className="btn btn-sm btn-danger"
              disabled={!isPending(row)}
              onClick={(e) => {
                e.stopPropagation();
                handleReject(row);
              }}
            >
              <XCircle size={14} className="me-1" />
              Reject
            </button>
          </div>
        ),
      },
    ],
    [handleApprove, handleReject],
  );

  const headerActions = [
    {
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      variant: 'outline',
      onClick: loadQueue,
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="APPROVAL SCREEN"
        description="Approval queue for lifecycle transitions with Approve/Reject actions."
        actions={headerActions}
      />

      <div className="page-container">
        <DataTable
          data={queue}
          columns={columns}
          loading={loading}
          showActions={false}
          searchable
          showFilterButton={false}
          onRowClick={(row) =>
            navigate('/sourcing/rfps/create/manual', {
              state: {
                rfpId: row._id,
                sourceRfpId: row._id,
                source: 'approval-queue',
              },
            })
          }
          emptyMessage="No RFP approvals available."
        />
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default RfpApprovalQueue;
